import { HttpErrorResponse } from '@angular/common/http';
import { Component, DestroyRef, EventEmitter, Input, OnInit, Output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { fadeInFadeOut } from '@animations/fade';
import { BOOKING_PAGE_VIEWS, CUSTOM_FIELD_TYPES } from '@constants/app.constants';
import { BOOKING_WIDGET_LOCAL_STORAGE_CONSTANTS } from '@constants/local-storage.constants';
import { CustomFieldModel } from '@models/custom-field.model';
import { CurrentViewData } from '@models/widget-conf.model';
import { TranslateModule } from '@ngx-translate/core';
import { BookingService } from '@services/feature/booking.service';
import { CustomFieldsService } from '@services/feature/custom-fields.service';
import { WidgetService } from '@services/feature/widget.service';
import { clearWorkerSelectAnswer } from '@services/utils/custom-field-tree.util';
import { HelperService } from '@services/utils/helper.service';
import { LocalstorageService } from '@services/utils/localstorage.service';
import { LoggerService } from '@services/utils/logger.service';
import { SwRouteService } from '@services/utils/sw-route.service';
import { ButtonComponent } from '@ui-lib/buttons/button/button.component';
import { BookingQuestionViewComponent } from '../booking-question-view/booking-question-view.component';


@Component({
  selector: 'app-booking-questions-view',
  templateUrl: './booking-questions-view.component.html',
  styleUrls: ['./booking-questions-view.component.scss'],
  animations: [fadeInFadeOut],
  imports: [BookingQuestionViewComponent, TranslateModule, ButtonComponent]
})
export class BookingQuestionsViewComponent implements OnInit {

  @Input() isFirstBookingQuestionActive = true;
  @Input() isLastBookingQuestionActive = false;
  @Input() customClass = '';
  // TRUE only on the BOOKING_QUESTIONS_VIEW_BEFORE mount (booking.component.html). The
  // before-step always renders the manually selected context questions via the legacy branch —
  // even when auto_load_booking_questions is ON — while the AFTER mount owns the service-resolved
  // set. Branching on the MODE alone is wrong since both mounts coexist in the combined mode.
  @Input() isBeforeQuestionsStep = false;

  counter = 0;
  widgetQuestions: CustomFieldModel[] = [];
  activeQuestion = 0;
  showError = false;
  errorMessage: string;
  errorTitle: string;

  // [auto_load_booking_questions] Mirrors the classic widget's isCustomFieldsLoaded gate: defaults
  // to true (legacy/flag-OFF path renders exactly as before — AC5), only flips to false while the
  // auto-load branch resolves so a soon-to-be-skipped empty step never flashes on screen.
  protected questionsLoaded = signal(true);
  // [auto_load_booking_questions review-fix #6] Loading-skeleton placeholders shown while
  // questionsLoaded() is false — mirrors booking-question-view's dependenciesGhostElements.
  protected questionsGhostElements = new Array<number>(2);

  @Output() nextPageEvent = new EventEmitter<CurrentViewData>();
  @Output() previousPageEvent = new EventEmitter<CurrentViewData>();

  constructor(
    private customFieldsService: CustomFieldsService,
    private helperService: HelperService,
    public widgetService: WidgetService,
    public bookingService: BookingService,
    private localstorageService: LocalstorageService,
    private swRouteService: SwRouteService,
    private destroyRef: DestroyRef,
  ) {
  }

  ngOnInit(): void {
    if (!this.isBeforeQuestionsStep && this.bookingService.isAutoLoadBookingQuestionsEnabled()) {
      this.questionsLoaded.set(false);
    }
    this.getCustomFieldsByIds();
    this.loadSelectedCustomFieldValues();
  }

  getCustomFieldsByIds(): void {
    if (!this.isBeforeQuestionsStep && this.bookingService.isAutoLoadBookingQuestionsEnabled()) {
      // [review-fix #5/#6] Wait for any fetch already in flight (triggered at SERVICES_VIEW) to
      // settle before deciding — emits immediately when nothing is pending, so this stays
      // synchronous in the common case. questionsLoaded() (set false in ngOnInit above) keeps the
      // loading skeleton (#6) up for the duration.
      // [review-fix, lifecycle] takeUntilDestroyed so a settle landing after this instance is
      // destroyed (e.g. the customer navigated away mid-fetch) never fires a wasted
      // context-only fetch against a torn-down component.
      this.bookingService.whenAutoLoadSettled().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.getAutoLoadedBookingQuestions());
      return;
    }

    // Legacy branch (flag OFF) — unchanged (AC3/AC5 byte-for-byte).
    if (this.widgetService.widgetConf?.context?.booking_questions?.length > 0) {
      this.customFieldsService.getCustomFieldsByIds(this.widgetService.widgetConf?.context?.booking_questions).subscribe({
        next: (result) => {
          if (result?.length) {
            // Translate any identifier-keyed prefill entries to their numeric custom-field id
            // once, before the prefill consumers read getCustomFieldValues().
            this.swRouteService.resolveCustomFieldIdentifiers(result);
            for (const customField of result) {
              if (customField.type === CUSTOM_FIELD_TYPES.CUSTOM_FIELD_SELECT
                || customField.type === CUSTOM_FIELD_TYPES.CUSTOM_FIELD_RADIO
                || customField.type === CUSTOM_FIELD_TYPES.CUSTOM_FIELD_SELECT_CARD
                || customField.type === CUSTOM_FIELD_TYPES.CUSTOM_FIELD_IMAGE_SELECT_CARD
                || customField.type === CUSTOM_FIELD_TYPES.CUSTOM_FIELD_NUMBER_SELECT_CARD) {
                if (customField.alphabetically_sorted === 1) {
                  customField.custom_field_options = this.helperService.sortByNumber(customField.custom_field_options, 'label');
                } else {
                  customField.custom_field_options = this.helperService.sortByNumber(customField.custom_field_options, 'position');
                }
              }
            }

            const widgetQuestions: CustomFieldModel[] = [];

            for (const id of this.widgetService.widgetConf?.context?.booking_questions) {
              for (const customField of result) {
                if (customField.id === id) {
                  if (customField.is_hidden === 0) {
                    widgetQuestions.push(customField);
                  } else {
                    this.bookingService.hiddenCustomFields.push(customField);
                  }
                }
              }
            }

            if (this.isFirstBookingQuestionActive) {
              this.activeQuestion = 0;
            }

            if (this.isLastBookingQuestionActive) {
              this.activeQuestion = widgetQuestions.length - 1;
            }

            this.widgetQuestions = widgetQuestions;
          } else {
            this.widgetQuestions = [];
          }
        },
        error: (error: HttpErrorResponse) => {
          LoggerService.warn('Error while fetching custom fields');
          LoggerService.error(error);
          if (error.status === 404) {
            this.errorTitle = 'error_messages.misconfiguration_error_title';
            this.errorMessage = 'error_messages.misconfiguration_error_message';
            this.showError = true;
          }
        }
      });
    }
  }

  // [auto_load_booking_questions] Renders resolvedBookingQuestions() (service-driven) unioned by id
  // with any context-only questions (ids present in context.booking_questions but not covered by the
  // resolved set — still fetched via the existing get_by_ids call), iterating the EFFECTIVE id list so
  // ordering/dedup covers both sources (AC2/AC4).
  private getAutoLoadedBookingQuestions(): void {
    if (this.bookingService.hasAutoLoadFetchFailed()) {
      // [review-fix #3] A genuine transport error must never be treated as "service has zero
      // questions" — surface the misconfiguration error instead of silently auto-skipping past a
      // BKB-required question. Mirrors the legacy branch's own 404 handling below.
      this.errorTitle = 'error_messages.misconfiguration_error_title';
      this.errorMessage = 'error_messages.misconfiguration_error_message';
      this.showError = true;
      this.questionsLoaded.set(true);
      return;
    }

    // The AFTER mount renders the placement target — the effective set minus the ids
    // the before-step already asked — so a qualification question answered up front never repeats.
    const effectiveIds = this.bookingService.getPlacementTargetQuestionIds();

    if (!effectiveIds?.length) {
      // Empty-set race guard (no-dead-step): nothing to ask -> skip straight to the personal form
      // instead of rendering a dead empty questions step.
      this.nextPageEvent.emit({ view: BOOKING_PAGE_VIEWS.PERSONAL_FORM_VIEW, isUserInteracted: false });
      return;
    }

    const resolvedQuestions = this.bookingService.resolvedBookingQuestions();
    const resolvedIds = resolvedQuestions.map(question => question.id);
    const contextOnlyIds = effectiveIds.filter(id => !resolvedIds.includes(id));

    if (!contextOnlyIds.length) {
      this.renderEffectiveBookingQuestions(effectiveIds, resolvedQuestions, []);
      return;
    }

    this.customFieldsService.getCustomFieldsByIds(contextOnlyIds).subscribe({
      next: (result) => {
        if (result?.length) {
          this.swRouteService.resolveCustomFieldIdentifiers(result);
          for (const customField of result) {
            if (customField.type === CUSTOM_FIELD_TYPES.CUSTOM_FIELD_SELECT
              || customField.type === CUSTOM_FIELD_TYPES.CUSTOM_FIELD_RADIO
              || customField.type === CUSTOM_FIELD_TYPES.CUSTOM_FIELD_SELECT_CARD
              || customField.type === CUSTOM_FIELD_TYPES.CUSTOM_FIELD_IMAGE_SELECT_CARD
              || customField.type === CUSTOM_FIELD_TYPES.CUSTOM_FIELD_NUMBER_SELECT_CARD) {
              customField.custom_field_options = customField.alphabetically_sorted === 1
                ? this.helperService.sortByNumber(customField.custom_field_options, 'label')
                : this.helperService.sortByNumber(customField.custom_field_options, 'position');
            }
          }
        }
        this.renderEffectiveBookingQuestions(effectiveIds, resolvedQuestions, result ?? []);
      },
      error: (error: HttpErrorResponse) => {
        LoggerService.warn('Error while fetching custom fields');
        LoggerService.error(error);
        // [review-fix N-C] Partial failure — only the context-only sub-list (the AC4 union's
        // manually-curated ids) failed; the service-resolved questions loaded fine. Render what
        // loaded instead of raising the blocking error banner, which the template renders as an
        // independent sibling *over* the live, answerable question form. Fall back to the
        // recoverable error only when nothing loaded at all, so a genuine total failure is never
        // silently auto-skipped past a required question (#3).
        if (resolvedQuestions.length) {
          LoggerService.warn(`Could not load context-only booking questions [${contextOnlyIds}]; rendering the ${resolvedQuestions.length} service-resolved question(s).`);
          this.renderEffectiveBookingQuestions(effectiveIds, resolvedQuestions, []);
          return;
        }
        this.errorTitle = 'error_messages.misconfiguration_error_title';
        this.errorMessage = 'error_messages.misconfiguration_error_message';
        this.showError = true;
        this.questionsLoaded.set(true);
      }
    });
  }

  // [review-fix N-B] The auto-load error state (#3) was a dead-end — no question/nav renders once
  // showError is true, leaving only a full page reload as a way out. Re-fires the resolve for the
  // currently selected service(s) instead of forcing a reload; a transient network error typically
  // just needs one more try.
  retryAutoLoadFetch(): void {
    this.showError = false;
    this.errorTitle = undefined;
    this.errorMessage = undefined;
    this.questionsLoaded.set(false);

    const serviceIds = this.bookingService.selectedBookingData?.currentSelectedAppointmentState?.selectedServiceIds ?? [];
    this.bookingService.resolveAutoLoadedBookingQuestions(serviceIds);
    this.bookingService.whenAutoLoadSettled().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.getAutoLoadedBookingQuestions());
  }

  // [review-fix N-B] The second recovery option from the auto-load error state: give up on this
  // service selection and let the customer pick a different one instead of retrying the same fetch.
  // No widgetQuestion was ever rendered in this state, so there is nothing to save first (unlike
  // previousQuestion(), which persists the current answer before navigating back).
  backToSlotsFromError(): void {
    this.previousPageEvent.emit({ view: BOOKING_PAGE_VIEWS.SLOTS_VIEW, isUserInteracted: true });
  }

  private renderEffectiveBookingQuestions(
    effectiveIds: number[],
    resolvedQuestions: CustomFieldModel[],
    contextOnlyQuestions: CustomFieldModel[]
  ): void {
    const widgetQuestions: CustomFieldModel[] = [];

    for (const id of effectiveIds) {
      const resolvedMatch = resolvedQuestions.find(question => question.id === id);
      if (resolvedMatch) {
        widgetQuestions.push(resolvedMatch);
        continue;
      }

      const contextMatch = contextOnlyQuestions.find(question => question.id === id);
      if (contextMatch) {
        if (contextMatch.is_hidden === 0) {
          widgetQuestions.push(contextMatch);
        } else {
          this.bookingService.hiddenCustomFields.push(contextMatch);
        }
      }
    }

    if (this.isFirstBookingQuestionActive) {
      this.activeQuestion = 0;
    }
    if (this.isLastBookingQuestionActive) {
      this.activeQuestion = widgetQuestions.length - 1;
    }

    if (!widgetQuestions.length) {
      // Every effective id resolved to hidden (or otherwise vanished) -> nothing left to ask.
      this.nextPageEvent.emit({ view: BOOKING_PAGE_VIEWS.PERSONAL_FORM_VIEW, isUserInteracted: false });
      return;
    }

    this.widgetQuestions = widgetQuestions;
    this.questionsLoaded.set(true);
  }

  // Fired when a child booking-question's preSelectStore() silently dropped an already-given
  // worker-select answer (the worker no longer belongs to the newly-selected store). Clears the
  // worker question's stale in-memory selection (closes the always-mounted staleness risk for
  // this view) and, if that question was already answered/passed, rewinds activeQuestion to it so
  // the user immediately re-answers against the filtered worker list. If the worker question is
  // still ahead of the current position, it will re-init filtered naturally — nothing to do.
  //
  // The worker-select question is not always top-level: it can be a customFieldDependencies
  // CHILD of another question (e.g. BKB's "Do you know your client advisor?" radio) — nested to
  // whatever depth the recursive #recursiveListTmpl in booking-question-view.component.html
  // renders. So each top-level question's full dependency chain is searched recursively; once
  // found, activeQuestion rewinds to the TOP-LEVEL ancestor's index (the recursive template only
  // renders a question's dependency chain while THAT top-level question is active), never to some
  // index of the nested question itself (nested questions have no index in widgetQuestions).
  // Idempotent: re-running with an already-cleared value / already-rewound activeQuestion is a
  // no-op both times.
  onWorkerSelectAnswerInvalidated(questionId?: number): void {
    const topLevelIndex = clearWorkerSelectAnswer(this.widgetQuestions, questionId);
    if (topLevelIndex > -1 && topLevelIndex < this.activeQuestion) {
      this.activeQuestion = topLevelIndex;
    }
  }

  previousQuestion(isFirst: boolean, currentWidgetQuestion: CustomFieldModel): void {
    this.setSelectedCustomFieldValues(currentWidgetQuestion);
    if (isFirst) {
      this.previousPageEvent.emit({ view: BOOKING_PAGE_VIEWS.SLOTS_VIEW, isUserInteracted: true });
    } else {
      this.activeQuestion = this.activeQuestion - 1;
    }
  }

  nextQuestion(currentWidgetQuestion: CustomFieldModel): void {
    this.setSelectedCustomFieldValues(currentWidgetQuestion);
    this.activeQuestion = this.activeQuestion + 1;
  }

  finishLastBookingQuestion(currentWidgetQuestion: CustomFieldModel): void {
    this.setSelectedCustomFieldValues(currentWidgetQuestion);
    // Route by MOUNT, not by mode: in the combined mode (before-questions + auto-load)
    // both mounts exist — the AFTER mount must continue to the personal form even though
    // isBeforeQuestionsMode() is true.
    if (this.isBeforeQuestionsStep) {
      if (this.bookingService.isMeetingTypeStepInFlow()) {
        this.nextPageEvent.emit({ view: BOOKING_PAGE_VIEWS.MEETING_TYPE_VIEW, isUserInteracted: true });
      } else {
        this.nextPageEvent.emit({ view: BOOKING_PAGE_VIEWS.STORE_VIEW, isUserInteracted: true });
      }
    } else {
      this.nextPageEvent.emit({ view: BOOKING_PAGE_VIEWS.PERSONAL_FORM_VIEW, isUserInteracted: true });
    }
  }

  setSelectedCustomFieldValues(currentWidgetQuestion: CustomFieldModel): void {
    const customFieldValues: {
      custom_field_id: number,
      type: string,
      value?: any, file_ids?: any,
      option_values?: {
        custom_field_option_id: number,
        custom_field_option_value: string
      }[],
      supported_appointment_service_ids?: number[],
      supported_meeting_type_ids?: number[]
    }[] = this.bookingService.setupCustomFieldSelectedValues(
      currentWidgetQuestion,
      this.bookingService.selectedBookingData.selectedCustomFieldValues
    );

    this.bookingService.selectedBookingData.selectedCustomFieldValues = customFieldValues;
    // This component renders twice: BEFORE (precedence 1) and AFTER (7, past STORE/SERVICES).
    // Only the BEFORE instance may re-arm — from AFTER it would re-hide a step already passed.
    this.bookingService.setCustomFieldValuesInLocalStorage(
      this.bookingService.selectedBookingData.selectedCustomFieldValues,
      this.isBeforeQuestionsStep
    );
  }

  loadSelectedCustomFieldValues(): void {
    const value: string = this.localstorageService.getItem(
      BOOKING_WIDGET_LOCAL_STORAGE_CONSTANTS.BOOKING_CUSTOM_FIELD + this.bookingService.partnerData.booking_name,
    );
    if (value) {
      this.bookingService.selectedBookingData.selectedCustomFieldValues = JSON.parse(value);
    }
  }

  increment(): void {
    this.counter++;
  }

  decrement(): void {
    this.counter--;
  }
}
